import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createCommunityTask } from '../services/api';

const CreateCommunityTask = ({ onClose, onTaskCreated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'cryptography',
    content: '',
    solution: '',
    hint: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validate form data
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Description is required');
      }
      if (!formData.content.trim()) {
        throw new Error('Content is required');
      }
      if (!formData.solution.trim()) {
        throw new Error('Solution is required');
      }
      
      // Check if user has enough WR coins (100)
      if ((user?.wrCoins || 0) < 100) {
        throw new Error(`You need 100 WR Coins to create a community task. You have ${user?.wrCoins || 0} WR Coins.`);
      }
      
      // Create the task
      const response = await createCommunityTask(formData);
      
      // Update local user data with new coin balance
      if (response.remainingCoins !== undefined) {
        // Update user's balance in localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          userData.wrCoins = response.remainingCoins;
          localStorage.setItem('user', JSON.stringify(userData));
        }
      }
      
      // Notify parent component and close modal
      onTaskCreated();
    } catch (err) {
      console.error('Error creating community task:', err);
      
      // Handle different types of errors
      if (err.response && err.response.status === 400) {
        const errorData = err.response.data;
        
        // Check if it's a content filter rejection
        if (errorData.reason === 'inappropriate_content') {
          setError(`❌ ${errorData.message}`);
        }
        // Check if it's validation errors
        else if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationMessages = errorData.errors.map(error => error.message).join(', ');
          setError(`❌ Validation Error: ${validationMessages}`);
        }
        // Check for general validation failure message
        else if (errorData.message === 'Validation failed') {
          setError('❌ Please check your form inputs. Some fields may be too short, too long, or contain invalid characters.');
        }
        // Other 400 errors
        else {
          setError(errorData.message || '❌ Invalid request. Please check your inputs.');
        }
      }
      // Handle other HTTP errors
      else if (err.response && err.response.data && err.response.data.message) {
        setError(`❌ ${err.response.data.message}`);
      }
      // Handle network or other errors
      else {
        setError(err.message || '❌ Failed to create task. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-task-overlay">
      <div className="create-task-container">
        {/* Header */}
        <div className="create-task-header">
          <div className="header-content">
            <div className="header-icon">📝</div>
            <div className="header-text">
              <h2>Create Community Task</h2>
              <p>Share your cybersecurity challenge with the community</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose} title="Close">
            <span>×</span>
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Cost Banner */}
        <div className="cost-banner">
          <div className="cost-info">
            <div className="cost-main">
              <span className="cost-icon">💰</span>
              <span className="cost-text">Cost: 100 WR Coins</span>
            </div>
            <div className="balance-info">
              <span>Your Balance: <strong>{user.wrCoins}</strong> WR</span>
              <span className={`status ${user.wrCoins >= 100 ? 'sufficient' : 'insufficient'}`}>
                {user.wrCoins >= 100 ? '✅ Ready to create' : '❌ Insufficient funds'}
              </span>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="create-task-form">
          <div className="form-content">
            
            {/* Basic Information */}
            <div className="form-section">
              <div className="section-header">
                <h3>📋 Basic Information</h3>
              </div>
              
              <div className="input-group">
                <label className="input-label">
                  Task Title
                  <span className="char-count">{formData.title.length}/100</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="text-input"
                  placeholder="Enter a compelling title for your task"
                  required
                  minLength="3"
                  maxLength="100"
                />
              </div>

              <div className="input-group">
                <label className="input-label">Category</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="select-input"
                  required
                >
                  <option value="cryptography">🔐 Cryptography</option>
                  <option value="coding">💻 Coding</option>
                  <option value="investigation">🔍 Investigation</option>
                  <option value="network">🌐 Network Security</option>
                  <option value="website">📱 Web Application</option>
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">
                  Description
                  <span className="char-count">{formData.description.length}/500</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="textarea-input"
                  placeholder="Provide a brief overview of what your task involves..."
                  required
                  minLength="10"
                  maxLength="500"
                  rows="3"
                />
              </div>
            </div>

            {/* Task Content */}
            <div className="form-section">
              <div className="section-header">
                <h3>📚 Task Content</h3>
              </div>
              
              <div className="input-group">
                <label className="input-label">
                  Challenge Content
                  <span className="char-count">{formData.content.length}/2000</span>
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  className="textarea-input large"
                  placeholder="Write the complete challenge here. Include all necessary instructions, code snippets, or problem statements..."
                  required
                  minLength="10"
                  maxLength="2000"
                  rows="8"
                />
              </div>

              <div className="form-grid">
                <div className="input-group">
                  <label className="input-label">
                    Solution
                    <span className="char-count">{formData.solution.length}/500</span>
                  </label>
                  <textarea
                    name="solution"
                    value={formData.solution}
                    onChange={handleChange}
                    className="textarea-input"
                    placeholder="Enter the correct answer or solution..."
                    required
                    minLength="1"
                    maxLength="500"
                    rows="4"
                  />
                </div>
                
                <div className="input-group">
                  <label className="input-label">
                    Hint (Optional)
                    <span className="char-count">{formData.hint.length}/200</span>
                  </label>
                  <textarea
                    name="hint"
                    value={formData.hint}
                    onChange={handleChange}
                    className="textarea-input"
                    placeholder="Provide a helpful hint for users who get stuck..."
                    maxLength="200"
                    rows="4"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-create"
              disabled={loading || user.wrCoins < 100}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Creating Task...
                </>
              ) : (
                <>
                  <span>✨</span>
                  Create Task (100 WR)
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCommunityTask;